import keyBy from "lodash/keyBy";
import React, { useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { usePrevious } from "react-use";

import {
  Drawer,
  IconButton,
  Text,
  Button,
  useToasts,
  DateInput,
  DateRangePicker,
  RangeValue,
  Toolbar,
  Select,
  ScrollArea,
} from "@puzzle/ui";
import { Add, Close } from "@puzzle/icons";
import { CalendarDate, parseDate, toCalendarDate, SHORT_DATE_FORMAT } from "@puzzle/utils";

import { useActiveCompany } from "components/companies/ActiveCompanyProvider";
import { useCompanyDateFormatter } from "components/companies/useCompanyDateFormatter";
import { PayrollJournalMapItemCategory } from "graphql/types";
import Analytics from "lib/analytics/analytics";

import { MiniHeader, Block, BlockText } from "./styles";
import {
  PayrollItemFragment,
  useUpdatePayrollMutation,
  useCreatePayrollMutation,
  PayrollDocument,
  PayrollsDocument,
} from "./graphql.generated";
import { UpsertPayrollTable } from "./UpsertPayrollTable";
import { usePayrollPage } from "./PayrollProvider";
import { AddPayrollAccountModal } from "./AddPayrollAccountModal";
import { Box } from "@puzzle/ve";
import { S, colors, vars } from "@puzzle/theme";
import { DATA_TEST_IDS } from "./constants";

type PayrollItemKey =
  | "netPay"
  | "salaries"
  | "employerTaxes"
  | "employeeTaxes"
  | "contractorPaymentAmount";

export type PayrollFormValues = {
  withdrawalDate: CalendarDate;
  checkDate: CalendarDate;
  payPeriod: RangeValue<[true, true]>;
  payrollAccountId: string;
  rows: {
    category?: PayrollJournalMapItemCategory;
    name?: string;
    amount?: number;
    benefitId?: string;
    key?: PayrollItemKey;
  }[];
};

const editSchema = yup.object({
  rows: yup
    .array()
    .of(
      yup.object({
        amount: yup.number().required(),
      })
    )
    .required(),
});

const createSchema = yup.object({
  withdrawalDate: yup.string().required(),
  checkDate: yup.string().required(),
  payPeriod: yup.array().of(yup.string().required()).required(),
  payrollAccountId: yup.string().required(),
  rows: yup
    .array()
    .of(
      yup.object({
        amount: yup.number().required(),
      })
    )
    .required(),
});

export type UpdateMode = "edit" | "create";

export const UpsertPayrollDrawer = ({
  open,
  onOpenChange,
  activePayroll,
  updateMode,
}: {
  open: boolean;
  onOpenChange?(open: boolean): void;
  activePayroll?: PayrollItemFragment;
  updateMode?: UpdateMode;
}) => {
  const { company } = useActiveCompany<true>();
  const { toast } = useToasts();
  const { payrollProviders, setActivePayroll } = usePayrollPage();
  const [addAccountOpen, setAddAccountOpen] = useState(false);
  const _updateMode = usePrevious(updateMode) ?? "edit";
  const [updatePayroll, { loading: isUpdating }] = useUpdatePayrollMutation({
    refetchQueries: [PayrollDocument, PayrollsDocument],
  });
  const [createPayroll, { loading: isCreating }] = useCreatePayrollMutation({
    refetchQueries: [PayrollDocument, PayrollsDocument],
  });

  const formatter = useCompanyDateFormatter({
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  });

  const form = useForm<PayrollFormValues>({
    mode: "onChange",
    resolver: yupResolver(_updateMode === "edit" ? editSchema : createSchema),
  });

  const handleUpsert = useMemo(() => {
    return form.handleSubmit(
      async ({ rows, withdrawalDate, checkDate, payPeriod, payrollAccountId }) => {
        const benefits = rows.filter(({ benefitId }) => !!benefitId);
        const { netPay, employeeTaxes, employerTaxes, salaries, contractorPaymentAmount } = keyBy(
          rows.filter(({ benefitId }) => !benefitId),
          "key"
        );

        const input = {
          companyId: company.id,
          netPay: netPay.amount?.toString() ?? "0",
          employeeTaxes: employeeTaxes.amount?.toString() ?? "0",
          employerTaxes: employerTaxes.amount?.toString() ?? "0",
          contractorPaymentAmount: contractorPaymentAmount.amount?.toString() ?? "0",
          benefits: benefits.map((benefit) => {
            return {
              id: benefit.benefitId ?? "",
              name: benefit.name ?? "",
              kind: benefit.category ?? PayrollJournalMapItemCategory.Employee,
              amount: benefit.amount?.toString() ?? "0",
            };
          }),
        };

        if (_updateMode === "edit" && activePayroll) {
          return await updatePayroll({
            variables: {
              input: {
                ...input,
                salaries: salaries.amount?.toString() ?? "0",
                payrollId: activePayroll.id,
              },
            },
            onCompleted: () => {
              Analytics.payrollUpdated({ id: activePayroll.id });
              toast({ message: "Payroll saved", status: "success" });
            },
            onError: () => {
              Analytics.payrollUpdateFailed({ id: activePayroll.id });
              toast({ message: "Payroll update failed. Please try again.", status: "error" });
            },
          });
        }

        return await createPayroll({
          variables: {
            input: {
              ...input,
              checkDate: checkDate.toString(),
              grossPay: salaries.amount?.toString() ?? "0",
              netPay: netPay.amount?.toString() ?? "0",
              payPeriod: {
                fromInclusive: payPeriod[0]!.toString(),
                toInclusive: payPeriod[1]!.toString(),
              },
              withdrawalDate: withdrawalDate.toString(),
              accountId: payrollAccountId,
            },
          },
          onCompleted: ({ createPayroll }) => {
            Analytics.payrollCreated();
            toast({ message: "Payroll created", status: "success" });

            setActivePayroll(createPayroll.id);
            onOpenChange?.(false);
          },
          onError: () => {
            Analytics.payrollCreateFailed();
            toast({ message: "Payroll create failed. Please try again.", status: "error" });
          },
        });
      }
    );
  }, [
    form,
    activePayroll,
    company.id,
    toast,
    updatePayroll,
    createPayroll,
    _updateMode,
    setActivePayroll,
    onOpenChange,
  ]);

  const selectFooter = useMemo(() => {
    return (
      <Button
        fullWidth
        size="small"
        variant="minimal"
        prefix={<Add size={14} />}
        onClick={() => setAddAccountOpen(true)}
        css={{ justifyContent: "left" }}
      >
        Add account
      </Button>
    );
  }, []);

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      css={{
        padding: 0,
        width: "100%",
        "@m": {
          width: 768,
        },
      }}
    >
      <AddPayrollAccountModal
        onOpenChange={() => setAddAccountOpen(!addAccountOpen)}
        open={addAccountOpen}
        payrollForm={form}
      />
      <Box css={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Box
          css={{
            display: "flex",
            justifyContent: "space-between",
            padding: `${S["1h"]} ${S["2"]} ${S["1h"]} ${S["3"]}`,
            alignItems: "center",
            borderBottom: `1px solid ${colors["outline-neutral-base"]}`,
          }}
        >
          <Text
            color="$text-neutral-base"
            css={{
              fontSize: "$bodyM",
              lineHeight: "$headingL",
              letterSpacing: "$bodyXS",
              fontWeight: "$bold",
            }}
          >
            {_updateMode === "create"
              ? "Create new payroll"
              : activePayroll &&
                `Edit payroll for ${formatter.format(parseDate(activePayroll.checkDate))}`}
          </Text>
          <IconButton onClick={() => onOpenChange?.(false)} autoFocus>
            <Close color="currentColor" size={12} />
          </IconButton>
        </Box>

        <ScrollArea css={{ flex: 1 }}>
          <ScrollArea.Viewport css={{ padding: "$3" }}>
            <Box css={{ display: "flex", marginBottom: S["2"], alignItems: "center" }}>
              <Toolbar
                css={{
                  display: "flex",
                  alignItems: "center",
                  ...(_updateMode === "create" ? { [`${Block}`]: { marginRight: 0 } } : {}),
                }}
              >
                {_updateMode === "edit" && activePayroll && (
                  <>
                    <Block>
                      <MiniHeader>Payroll provider</MiniHeader>
                      <BlockText>{activePayroll.providerName}</BlockText>
                    </Block>

                    <Block>
                      <MiniHeader>Withdrawal date</MiniHeader>
                      <BlockText>
                        {formatter.format(parseDate(activePayroll.withdrawalDate))}
                      </BlockText>
                    </Block>

                    <Block>
                      <MiniHeader>Posting date</MiniHeader>
                      <BlockText>{formatter.format(parseDate(activePayroll.checkDate))}</BlockText>
                    </Block>
                  </>
                )}
                {_updateMode === "create" && (
                  <>
                    <Block>
                      <MiniHeader>Payroll provider</MiniHeader>
                      <BlockText>
                        <Controller
                          control={form.control}
                          name="payrollAccountId"
                          render={({ field }) => {
                            return (
                              <Select
                                instruction="Pick a provider"
                                size="small"
                                value={field.value}
                                footer={selectFooter}
                                onSelectionChange={(value) => {
                                  field.onChange({
                                    target: {
                                      value,
                                      name: field.name,
                                    },
                                  });
                                }}
                                options={payrollProviders.map((p) => {
                                  return { label: p.institutionName, value: p.accountId };
                                })}
                                css={{ width: "100%" }}
                              />
                            );
                          }}
                        />
                      </BlockText>
                    </Block>

                    <Block>
                      <MiniHeader>Posting date</MiniHeader>
                      <Controller
                        control={form.control}
                        name="checkDate"
                        render={({ field }) => {
                          return (
                            <DateInput
                              size="small"
                              iconSide="right"
                              value={field.value}
                              onChange={(value) => {
                                field.onChange({
                                  target: {
                                    value: value && toCalendarDate(value),
                                    name: field.name,
                                  },
                                });
                              }}
                            />
                          );
                        }}
                      />
                    </Block>

                    <Block>
                      <MiniHeader>Withdrawal date</MiniHeader>
                      <Controller
                        control={form.control}
                        name="withdrawalDate"
                        render={({ field }) => {
                          return (
                            <DateInput
                              size="small"
                              iconSide="right"
                              value={field.value}
                              onChange={(value) => {
                                field.onChange({
                                  target: {
                                    value: value && toCalendarDate(value),
                                    name: field.name,
                                  },
                                });
                              }}
                            />
                          );
                        }}
                      />
                    </Block>

                    <Block>
                      <MiniHeader>Pay period</MiniHeader>
                      <Controller
                        control={form.control}
                        name="payPeriod"
                        render={({ field }) => {
                          return (
                            <DateRangePicker
                              placeholderText="Select a range"
                              showLabel={false}
                              value={field.value ?? [undefined, undefined]}
                              allowEmpty={[true, true]}
                              view="day"
                              customLabelFormat={SHORT_DATE_FORMAT}
                              onChange={([s, e]) => {
                                const start = s && toCalendarDate(s);
                                const end = e && toCalendarDate(e);

                                field.onChange({
                                  target: {
                                    value: [start, end],
                                    name: field.name,
                                  },
                                });
                              }}
                            />
                          );
                        }}
                      />
                    </Block>
                  </>
                )}
              </Toolbar>
            </Box>

            <UpsertPayrollTable
              activePayroll={activePayroll}
              form={form}
              updateMode={_updateMode}
            />
          </ScrollArea.Viewport>

          <ScrollArea.Scrollbar orientation="vertical" css={{ width: "10px !important" }}>
            <ScrollArea.Thumb variant="shadowed" />
          </ScrollArea.Scrollbar>
        </ScrollArea>

        <Box
          css={{
            padding: `${S["2"]} ${S["2"]} ${S["11h"]} ${S["2"]}`,
            borderTop: "1px solid transparent",
            borderColor: vars.colors["outline-neutral-subdue"],
          }}
        >
          <Box css={{ display: "flex", gap: S["2"], justifyContent: "flex-end" }}>
            <Button
              data-testid={DATA_TEST_IDS.PAYROLL_CREATE_DRAWER_CANCEL}
              variant="minimal"
              onClick={() => onOpenChange?.(false)}
            >
              Cancel
            </Button>
            <Button
              data-testid={DATA_TEST_IDS.PAYROLL_CREATE_DRAWER_SAVE}
              disabled={!form.formState.isValid || !form.formState.isDirty}
              onClick={handleUpsert}
              loading={isUpdating || isCreating}
            >
              {_updateMode === "create" ? "Create payroll" : "Save"}
            </Button>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};
